import { Briefcase, Building2, CheckCircle2, ChevronDown, DollarSign, FolderKanban, Sparkles, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_COLORS = { Planning: "#A1A1AA", "In Progress": "#2563EB", Blocked: "#DC2626", Completed: "#10B981" };

const formatCurrency = (value) => value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `$${(value / 1000).toFixed(1)}K` : `$${value || 0}`;
const formatDate = (value) => {
  if (!value) return "No date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const Dashboard = ({ data, onGenerateAiBrief, aiBriefLoading, onOpenProject }) => {
  if (!data) return <div className="empty-state"><p className="text-sm">Loading dashboard...</p></div>;

  const report = data.smart_report;
  const statusData = (data.projects_by_status || []).map((item) => ({ name: item.status, value: item.count, color: STATUS_COLORS[item.status] || "#A1A1AA" }));
  const budgetData = (data.budget_by_company || []).slice(0, 8).map((item) => ({ name: item.company_name?.length > 18 ? `${item.company_name.substring(0, 18)}...` : item.company_name, budget: item.total_budget }));
  const attentionCount = (report?.totals?.overdue_tasks || 0) + (report?.totals?.blocked_projects || 0);

  return (
    <div className="simple-dashboard" data-testid="dashboard-view">
      <header className="dashboard-heading">
        <div>
          <p className="dashboard-eyebrow">Portfolio overview</p>
          <h1>Dashboard</h1>
        </div>
        {report ? (
          <div className="dashboard-health" data-tone={attentionCount ? "attention" : "good"}>
            <span>{report.portfolio_health?.label || "No rating"}</span>
            <strong>{report.portfolio_health?.score ?? 0}</strong>
          </div>
        ) : null}
      </header>

      <section className="dashboard-stat-grid" aria-label="Portfolio totals">
        <StatCard label="Companies" value={data.total_companies || 0} icon={<Briefcase size={18} />} />
        <StatCard label="Businesses" value={data.total_businesses || 0} icon={<Building2 size={18} />} />
        <StatCard label="Projects" value={data.total_projects || 0} icon={<FolderKanban size={18} />} />
        <StatCard label="Budget" value={formatCurrency(data.total_budget || 0)} icon={<DollarSign size={18} />} />
      </section>

      {report ? (
        <>
          <section className="dashboard-focus-card">
            <div className="dashboard-section-heading">
              <div>
                <p className="dashboard-eyebrow">Today</p>
                <h2>Needs attention</h2>
              </div>
              <div className="dashboard-quick-metrics">
                <QuickMetric label="Complete" value={`${report.portfolio_health?.completion_rate ?? 0}%`} tone="good" />
                <QuickMetric label="Overdue" value={report.totals?.overdue_tasks ?? 0} tone={(report.totals?.overdue_tasks || 0) ? "danger" : "muted"} />
                <QuickMetric label="Unassigned" value={report.totals?.unassigned_tasks ?? 0} tone={(report.totals?.unassigned_tasks || 0) ? "warning" : "muted"} />
                <QuickMetric label="Blocked" value={report.totals?.blocked_projects ?? 0} tone={(report.totals?.blocked_projects || 0) ? "danger" : "muted"} />
              </div>
            </div>

            <div className="dashboard-action-grid">
              <ActionList
                emptyText="No overdue tasks"
                items={(report.overdue_tasks || []).slice(0, 5)}
                onItemClick={(item) => onOpenProject?.(item.project_id, item.id)}
                title="Overdue"
                renderItem={(item) => <><strong>{item.title}</strong><span>{item.project_name} · {Math.abs(item.due_in_days || 0)} days late</span></>}
              />
              <ActionList
                emptyText="No upcoming deadlines"
                items={(report.upcoming_deadlines || []).slice(0, 5)}
                onItemClick={(item) => onOpenProject?.(item.project_id, item.id)}
                title="Upcoming"
                renderItem={(item) => <><strong>{item.title}</strong><span>{item.project_name} · {formatDate(item.due_date)}</span></>}
              />
              <ActionList
                emptyText="No priority projects"
                items={(report.priority_projects || []).slice(0, 5)}
                onItemClick={(item) => onOpenProject?.(item.project_id)}
                title="Priority projects"
                renderItem={(item) => <><strong>{item.project_name}</strong><span>{item.open_task_count} open · {item.overdue_task_count} overdue</span></>}
              />
            </div>
          </section>

          <details className="dashboard-insights">
            <summary>
              <span><strong>Insights and reports</strong><small>Charts, team load, budget and AI summary</small></span>
              <ChevronDown size={18} />
            </summary>
            <div className="dashboard-insights-body">
              <div className="dashboard-insight-grid">
                <section className="dashboard-report-card">
                  <h3>Projects by status</h3>
                  {statusData.length ? <div className="dashboard-chart"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={statusData} cx="50%" cy="50%" innerRadius={48} outerRadius={74} paddingAngle={2} dataKey="value">{statusData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer></div> : <EmptyReport text="No project data" />}
                </section>
                <section className="dashboard-report-card">
                  <h3>Budget by company</h3>
                  {budgetData.length ? <div className="dashboard-chart"><ResponsiveContainer width="100%" height="100%"><BarChart data={budgetData} layout="vertical" margin={{ left: 16 }}><CartesianGrid strokeDasharray="3 3" stroke="var(--dashboard-grid, #E4E4E7)" /><XAxis type="number" tickFormatter={formatCurrency} /><YAxis type="category" dataKey="name" width={115} /><Tooltip formatter={(value) => formatCurrency(value)} /><Bar dataKey="budget" fill="#2563EB" /></BarChart></ResponsiveContainer></div> : <EmptyReport text="No budget data" />}
                </section>
              </div>

              <div className="dashboard-insight-grid">
                <ReportList title="Team load" icon={<Users size={16} />} items={report.workload || []} renderItem={(item) => <><strong>{item.member_name}</strong><span>{item.active_tasks} active · {item.overdue_tasks} overdue</span></>} />
                <ReportList title="Budget hotspots" icon={<DollarSign size={16} />} items={report.budget_hotspots || []} renderItem={(item) => <><strong>{item.company_name}</strong><span>{formatCurrency(item.total_budget)} · {item.share_of_budget}%</span></>} />
              </div>

              <section className="dashboard-ai-card">
                <div className="dashboard-section-heading">
                  <div><p className="dashboard-eyebrow">AI assistant</p><h2>Executive brief</h2></div>
                  <button className="btn-primary inline-flex items-center gap-2" disabled={aiBriefLoading} onClick={onGenerateAiBrief} type="button"><Sparkles size={14} />{aiBriefLoading ? "Generating..." : "Generate brief"}</button>
                </div>
                {report.ai_brief ? <AiBrief brief={report.ai_brief} /> : <p className="dashboard-empty-copy">{report.ai_brief_error || (report.ai?.configured ? "No AI brief generated yet." : "AI is not configured on the server.")}</p>}
              </section>
            </div>
          </details>
        </>
      ) : null}
    </div>
  );
};

const StatCard = ({ label, value, icon }) => <div className="dashboard-stat"><span>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></div>;
const QuickMetric = ({ label, value, tone }) => <div className="dashboard-quick-metric" data-tone={tone}><strong>{value}</strong><span>{label}</span></div>;

const ActionList = ({ title, items, renderItem, emptyText, onItemClick }) => (
  <div className="dashboard-action-list">
    <h3>{title}</h3>
    {items.length ? items.map((item, index) => <button key={item.id || item.project_id || index} onClick={() => onItemClick(item)} type="button">{renderItem(item)}</button>) : <div className="dashboard-list-empty"><CheckCircle2 size={16} /><span>{emptyText}</span></div>}
  </div>
);

const ReportList = ({ title, icon, items, renderItem }) => (
  <section className="dashboard-report-card dashboard-report-list">
    <h3>{icon}{title}</h3>
    {items.length ? items.slice(0, 6).map((item, index) => <div key={item.id || item.member_name || item.company_id || index}>{renderItem(item)}</div>) : <EmptyReport text={`No ${title.toLowerCase()} data`} />}
  </section>
);

const EmptyReport = ({ text }) => <div className="dashboard-empty-copy">{text}</div>;

const AiBrief = ({ brief }) => (
  <div className="dashboard-ai-content">
    <p>{brief.summary}</p>
    <div><ListBlock title="Priorities" items={brief.priorities} /><ListBlock title="Risks" items={brief.risks} /><ListBlock title="Opportunities" items={brief.opportunities} /></div>
  </div>
);

const ListBlock = ({ title, items = [] }) => (
  <section><h3>{title}</h3>{items.length ? <ul>{items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}</ul> : <p>None.</p>}</section>
);
